import { Controller } from "@hotwired/stimulus"

// Kontroler generuje pola formularza na podstawie schematu bazy Noe::Db
// Obsługuje zarówno single-table jak i multi-table schemas
export default class extends Controller {
  static targets = ["fieldsContainer", "fieldsCard", "dataField", "rawField", "tableSelect"]
  static values = {
    schema: Object,
    multiTable: Boolean,
    currentTable: String,
  }

  connect() {
    this.buildFields()
    this.loadDataToFields()
    this.syncRawField()
  }

  // Zwraca pola dla aktualnej tabeli (lub całego schematu dla single-table)
  getFields() {
    if (this.multiTableValue) {
      const tableName = this.currentTableValue || this.tableSelectTarget?.value
      if (!tableName) return {}
      const tableConfig = this.schemaValue.tables?.[tableName] || {}
      return tableConfig.fields || {}
    }
    return this.schemaValue.fields || {}
  }

  // Obsługa zmiany tabeli
  onTableChange() {
    this.currentTableValue = this.tableSelectTarget.value
    this.buildFields()
    // Wyczyść dane przy zmianie tabeli (dla nowego rekordu)
    if (!this.dataFieldTarget.value || this.dataFieldTarget.value === "{}") {
      this.fieldsContainerTarget.querySelectorAll("input, select, textarea").forEach((input) => {
        input.value = ""
      })
    }
    this.loadDataToFields()
    this.syncRawField()
  }

  // Normalizuje definicję pola: "string" -> { type: "string" }
  normalizeFieldDef(fieldDef) {
    if (typeof fieldDef === "string") {
      return { type: fieldDef }
    }
    return fieldDef
  }

  // Buduje pola HTML na podstawie schematu
  buildFields() {
    const fields = this.getFields()
    const container = this.fieldsContainerTarget

    container.innerHTML = ""

    // Dla multi-table bez wybranej tabeli - pokaż komunikat
    if (this.multiTableValue && !this.currentTableValue && !this.tableSelectTarget?.value) {
      container.innerHTML = "<p class=\"text-gray-500 text-sm\">Wybierz tabelę aby zobaczyć pola</p>"
      return
    }

    // Brak pól w schemacie - pokaż komunikat
    if (Object.keys(fields).length === 0) {
      container.innerHTML = "<p class=\"text-gray-500 text-sm\">Brak zdefiniowanych pól w schemacie</p>"
      return
    }

    Object.entries(fields).forEach(([fieldName, fieldDef], index) => {
      const normalizedDef = this.normalizeFieldDef(fieldDef)
      const fieldElement = this.createFieldElement(fieldName, normalizedDef, index === 0)
      container.appendChild(fieldElement)
    })
  }

  // Tworzy pojedyncze pole formularza
  createFieldElement(fieldName, fieldDef, autofocus = false) {
    const wrapper = document.createElement("div")
    wrapper.className = "mb-4"

    const label = document.createElement("label")
    label.className = "block text-sm font-medium text-gray-700 mb-1"
    label.textContent = fieldName
    if (fieldDef.required) {
      const required = document.createElement("span")
      required.className = "text-red-500 ml-1"
      required.textContent = "*"
      label.appendChild(required)
    }

    const input = this.createInputForType(fieldName, fieldDef, autofocus)

    wrapper.appendChild(label)
    wrapper.appendChild(input)

    // Dodaj opis typu
    if (fieldDef.type) {
      const hint = document.createElement("p")
      hint.className = "mt-1 text-xs text-gray-500"
      hint.textContent = `Typ: ${fieldDef.type}`
      wrapper.appendChild(hint)
    }

    return wrapper
  }

  // Tworzy input odpowiedniego typu
  createInputForType(fieldName, fieldDef, autofocus) {
    const type = fieldDef.type || "string"
    let input

    const baseClasses =
      "block w-full rounded-lg border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"

    switch (type) {
      case "text":
        input = document.createElement("textarea")
        input.rows = 4
        input.className = baseClasses
        break

      case "integer":
      case "number":
        input = document.createElement("input")
        input.type = "number"
        input.className = baseClasses
        if (type === "integer") {
          input.step = "1"
        }
        break

      case "boolean":
        input = document.createElement("select")
        input.className = baseClasses
        input.innerHTML = `
          <option value="">-- wybierz --</option>
          <option value="true">Tak</option>
          <option value="false">Nie</option>
        `
        break

      case "date":
        input = document.createElement("input")
        input.type = "date"
        input.className = baseClasses
        break

      case "time":
        input = document.createElement("input")
        input.type = "time"
        input.className = baseClasses
        break

      case "datetime":
        input = document.createElement("input")
        input.type = "datetime-local"
        input.className = baseClasses
        break

      default: // string
        input = document.createElement("input")
        input.type = "text"
        input.className = baseClasses
        break
    }

    input.dataset.fieldName = fieldName
    input.dataset.action = "input->record-form#updateData"

    if (fieldDef.required) {
      input.required = true
    }

    if (autofocus && !this.multiTableValue) {
      input.autofocus = true
    }

    return input
  }

  // Ładuje dane z pola data do pól formularza
  loadDataToFields() {
    try {
      const data = JSON.parse(this.dataFieldTarget.value || "{}")
      const container = this.fieldsContainerTarget

      Object.entries(data).forEach(([fieldName, value]) => {
        const input = container.querySelector(`[data-field-name="${fieldName}"]`)
        if (input) {
          this.setInputValue(input, value)
        }
      })
    } catch (e) {
      console.warn("Nie można sparsować danych JSON:", e)
    }
  }

  // Ustawia wartość inputa
  setInputValue(input, value) {
    if (value === null || value === undefined) {
      input.value = ""
      return
    }

    if (input.tagName === "SELECT" && typeof value === "boolean") {
      input.value = value.toString()
    } else if (input.type === "datetime-local" && value) {
      const date = new Date(value)
      if (!isNaN(date)) {
        input.value = date.toISOString().slice(0, 16)
      }
    } else if (input.type === "date" && value) {
      const date = new Date(value)
      if (!isNaN(date)) {
        input.value = date.toISOString().slice(0, 10)
      }
    } else {
      input.value = value
    }
  }

  // Aktualizuje pole data na podstawie pól formularza
  updateData() {
    const data = {}
    const container = this.fieldsContainerTarget
    const fields = this.getFields()

    Object.keys(fields).forEach((fieldName) => {
      const input = container.querySelector(`[data-field-name="${fieldName}"]`)
      if (input) {
        const normalizedDef = this.normalizeFieldDef(fields[fieldName])
        const value = this.getInputValue(input, normalizedDef)
        if (value !== null && value !== undefined && value !== "") {
          data[fieldName] = value
        }
      }
    })

    this.dataFieldTarget.value = JSON.stringify(data, null, 2)
    this.syncRawField()
  }

  // Synchronizuje pole rawField z dataField
  syncRawField() {
    if (this.hasRawFieldTarget) {
      this.rawFieldTarget.value = this.dataFieldTarget.value
    }
  }

  // Synchronizacja z pola raw JSON do pól formularza
  syncFromRaw() {
    if (!this.hasRawFieldTarget) return

    try {
      const data = JSON.parse(this.rawFieldTarget.value || "{}")
      this.dataFieldTarget.value = JSON.stringify(data, null, 2)
      this.loadDataToFields()
    } catch (_e) {
      // Ignoruj błędy parsowania podczas edycji
    }
  }

  // Pobiera wartość z inputa i konwertuje do odpowiedniego typu
  getInputValue(input, fieldDef) {
    const type = fieldDef?.type || "string"
    const value = input.value

    if (value === "") {
      return null
    }

    switch (type) {
      case "integer":
        return parseInt(value, 10)

      case "number":
        return parseFloat(value)

      case "boolean":
        return value === "true"

      case "datetime":
      case "date":
      case "time":
        return value || null

      default:
        return value
    }
  }
}
